import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { ArrowLeft } from "@untitledui/icons";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { SessionRosterPage } from "@/features/admin/instructor-led/SessionRosterPage";
import { SessionResourcesSection } from "@/features/instructor-led/resources/SessionResourcesSection";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";

export const Route = createFileRoute("/_app/my-sessions_/$sessionId")({
  // Hidden while the ilt flag is off (the API 404s the roster too): back to My Learning.
  beforeLoad: async () => {
    const { ilt } = await fetchFeatureFlags();
    if (!ilt) throw redirect({ to: "/my-learning" });
  },
  component: TrainerSessionRoute,
  remountDeps: ({ params }) => params.sessionId,
});

const linkClassName =
  "text-arc-primary-900 inline-flex items-center gap-1 rounded text-sm font-semibold underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2";

/**
 * One of a trainer's sessions (LMS-203): its course, date, time and venue, and its roster, where
 * the trainer marks attendance once it has started, as an admin does (LMS-38), and
 * the files and links the trainer shares with its learners (LMS-143). A session the trainer isn't
 * assigned to, or has been taken off, is Not found.
 */
function TrainerSessionRoute() {
  const { sessionId } = Route.useParams();

  return (
    <ProtectedRoute
      requiredPermissions={["IltSessions.Deliver"]}
      requiredRoles={["Trainer"]}
      redirectTo="/my-learning"
    >
      <SessionRosterPage
        sessionId={sessionId}
        renderResources={() => <SessionResourcesSection sessionId={sessionId} />}
        backLink={
          <Link to="/my-sessions" className={linkClassName}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            Back to my sessions
          </Link>
        }
        renderUnavailable={() => (
          <div role="alert" className="flex flex-col gap-2">
            <h1 className="text-primary text-3xl font-semibold">Not found</h1>
            <p className="text-tertiary text-sm">
              This session isn&apos;t one of yours, or it no longer exists.
            </p>
          </div>
        )}
      />
    </ProtectedRoute>
  );
}
